/// <amd-module name="Dashboards/DashboardPanel/ManageDashboardPanel" />
import { Dashboard, DashboardScope } from "Dashboards/Common/Contracts/Dashboard";
import { TeamContext } from "Dashboards/Common/Contracts/TeamContext";
import { IDashboardRestClient } from "Dashboards/Common/RestClient/Dashboard";
import {
    Constants,
    FormFields,
    Project,
    Team,
    WebAccessDashboardsSecurityDialog,
    SecurityNameSpaceIdForDashboards
} from "Dashboards/DashboardPanel/Common";
import { DashboardPanel } from "Dashboards/DashboardPanel/DashboardPanel";
import * as Resources from "Dashboards/DashboardPanel/Resources";
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssComponentContext, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IIdentity } from "VSSUI/IdentityPicker";
import { Link } from "VSSUI/Link";

import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface ManageDashboardPanelProps {
    dashboard: Dashboard;
    onDashboardUpdated: (updatedDashboard: Dashboard) => void;
    canManagePermissionsForDashboard: boolean;
    onDismiss: () => void;
    project: Project;
    team?: Team;
    owner?: IIdentity;
}

interface ManageDashboardPanelState {
    hidePanel: boolean;
}

export class ManageDashboardPanel extends VssComponent<ManageDashboardPanelProps, ManageDashboardPanelState> {
    public static readonly teamScopedGroupId = "00000000-0000-0000-0000-000000000000";

    constructor(props: ManageDashboardPanelProps, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            hidePanel: false
        };
    }

    public render(): JSX.Element {
        const { dashboard, team, owner, canManagePermissionsForDashboard, onDismiss } = this.props;
        const isTeamDashboard = !this.isProjectDashboard();

        return (
            <DashboardPanel
                allTeams={[]}
                dashboard={dashboard}
                dashboardTeam={team}
                dashboardOwner={owner}
                description={Resources.ManageDashboardPanelDescription}
                disableOwnerField={isTeamDashboard || !canManagePermissionsForDashboard}
                hidePanel={this.state.hidePanel}
                onDismiss={onDismiss}
                onSubmit={this.handleSubmit}
                submitButtonLabel={Resources.ManageDashboardSubmitButtonLabel}
                showOwnerField
                title={(panelName: string) => format(Resources.ManageDashboardPanelTitle, panelName)}
                userTeams={[]}
                dashboardProject={this.props.project}
            >
                {this.renderSecuritySection()}
            </DashboardPanel>
        );
    }

    private renderSecuritySection(): JSX.Element {
        return (
            <div className="manage-dashboard-panel-security-section">
                <div className="manage-dashboard-panel-security-section-heading">{Resources.SecuritySectionHeading}</div>
                <div className="manage-dashboard-panel-security-section-description">{this.getSecuritySectionDescription()}</div>
                <Link onClick={this.onManagePermissionsClick}>{Resources.ManagePermissionsLink}</Link>
            </div>
        );
    }

    private isProjectDashboard(): boolean {
        return this.props.dashboard.dashboardScope === DashboardScope.Project;
    }

    private getSecuritySectionDescription(): string {
        return this.isProjectDashboard() ? Resources.SecuritySectionProjectDashboardDescription : Resources.SecuritySectionTeamDashboardDescription;
    }

    private handleSubmit = (fields: FormFields): Promise<void> => {
        return new Promise((resolve, reject) => {
            const dashboard = {
                ...this.props.dashboard,
                name: fields.name,
                description: fields.description,
                refreshInterval: fields.autoRefresh ? Constants.autoRefreshIntervalInMins : 0,
                ownerId: fields.owner ? fields.owner.localId : undefined
            } as Dashboard;

            let teamContext: TeamContext = {
                projectId: this.props.project.id,
                project: this.props.project.name,
                teamId: "",
                team: ""
            };

            if (this.props.team) {
                teamContext = {
                    ...teamContext,
                    teamId: this.props.team.id,
                    team: this.props.team.name
                };
            }

            const dashboardRestClient = this.context.pageContext.getRestClient<IDashboardRestClient>("IDashboardRestClient");
            dashboardRestClient
                .replaceDashboard(dashboard, teamContext, dashboard.id)
                .then((updatedDashboard: Dashboard) => {
                    this.props.onDashboardUpdated(updatedDashboard);
                    resolve();
                })
                .catch((error: Error) => {
                    reject(error);
                });
        });
    };

    private onManagePermissionsClick = () => {
        this.setState({ hidePanel: true }, () => {
            const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");

            layoutManager.renderCallout(onDismiss => {
                const teamId = this.props.team ? this.props.team.id : ManageDashboardPanel.teamScopedGroupId;

                if (isFeatureFlagEnabled(this.context.pageContext, WebAccessDashboardsSecurityDialog, false)) {
                    const wrappedProps = {
                        onCancel: () => {
                            onDismiss();
                            this.setState({ hidePanel: false });
                        },
                        securityViewTitle: this.props.dashboard.name,
                        permissionSetId: SecurityNameSpaceIdForDashboards,
                        permissionSetToken: JSON.stringify({
                            teamId: teamId,
                            dashboardId: this.props.dashboard.id
                        })
                    };

                    return (
                        <WrappedComponent
                            dependencies={["ms.vss-admin-web.security-view-common-content"]}
                            wrappedType="ms.vss-admin-web.security-panel-component"
                            wrappedProps={wrappedProps}
                        />
                    );
                } else {
                    const wrappedProps = {
                        dashboardId: this.props.dashboard.id,
                        dashboardName: this.props.dashboard.name,
                        teamId: teamId,
                        onClose: () => {
                            onDismiss();
                            this.setState({ hidePanel: false });
                        }
                    };

                    return (
                        <LegacyComponent
                            modules={["Dashboards/Components/Directory/DashboardsSecurityDialog"]}
                            wrappedType="DashboardSecurityDialog"
                            wrappedProps={wrappedProps}
                        />
                    );
                }
            });
        });
    };
}
